/* @flow */
// typecheck: ignore file
import _ from 'lodash';
import CurrentUser from 'auth/CurrentUser';
import Group from 'entities/Group';
import Groups from 'collections/Groups';
import Organization from 'entities/Organization';
import Organizations from 'collections/Organizations';
import { PropTypes } from 'react';
import { isAdmin, isPendingAdmin } from 'shapes/Affiliation';
import { rootGroup } from 'entities/RootGroup';

const scopeRank = {
  class: 1,
  district: 2,
  district_organization: 3,
  school: 4,
  root: 5,
};

const adminScopeRank = {
  district: 1,
  district_organization: 2,
  school: 3,
  class: 4,
  root: 5,
};

// 1. If there exists a district, rank the district first, followed by schools, owned groups,
// joined groups, all classes

// 2. If there is no district, and there exist any schools for which my relationship to that school
// is 'pending admin' or 'admin', then  schools -> owned classes -> joined classes -> all classes

// 3. Otherwise, owned classes - joined classes -> schools -> all classes
const sectionOrdering = (a: SectionInfo, b: SectionInfo) => {
  return scopeRank[a.scope] - scopeRank[b.scope];
};

const adminComparator = (a: SectionInfo, b: SectionInfo) => {
  return adminScopeRank[a.scope] - adminScopeRank[b.scope];
};

/**
 * The organization model pulled from an endpoint that included
 * 'user_affiliation' to indicate the requesting users's affiliation with the
 * org.
 * @param  {Organization} organization from the v2/organizations endpoint
 * @return {Boolean}    whether current user is or could be an admin of the org
 */
export const isPossibleAdminOf = (organization: Organization) => {
  const affiliation = organization && organization.get('user_affiliation');

  return affiliation && (isAdmin(affiliation) || isPendingAdmin(affiliation));
};

const possibleAdminsOfType = (organizations: Organizations, type) => {
  const typeAdminFilter = org =>
    org.get('type') === type && isPossibleAdminOf(org);

  return organizations.filter(typeAdminFilter);
};

export const getDistrictsOrganizationInfo = (organizations: Organizations): SectionInfo => {
  const items = possibleAdminsOfType(organizations, 'District');
  const hasPossibleAdmins = items.length > 0;

  return {
    shouldRender: hasPossibleAdmins,
    scope: 'district_organization',
    key: 'district_organizations',
    items,
  };
};

/**
 * If the user is an admin or teacher in the org, they are a member
 * of the org's staff class.
 *
 * If the user is a pending admin, there may not be a staff class.
 * In that case we need to render an item from the organization model.
 */
export const getSchoolsInfo = (organizations: Organizations, schoolGroups: Groups): SectionInfo => {
  const schoolsPossiblyAdmined = possibleAdminsOfType(organizations, 'School');

  const couldAdminCount = schoolsPossiblyAdmined.length +
    schoolGroups.filter(g => g.isOwnedByUser(CurrentUser.getUserId())).length;

  const orgIdsOfStaffClasses = schoolGroups.map(g => g.getStaffClassOrgId());
  const schoolsWithoutStaffClasses = schoolsPossiblyAdmined.filter(s =>
    !orgIdsOfStaffClasses.includes(s.id)
  );

  const items = schoolGroups.models.concat(schoolsWithoutStaffClasses);

  return {
    shouldRender: items.length > 0,
    containsAdminSchools: couldAdminCount > 0,
    scope: 'school',
    key: 'schools',
    items,
  };
};

export const getDistrictsInfo = (districtGroups: Groups): SectionInfo => {
  const couldAdminCount = districtGroups.filter(g => isPossibleAdminOf(g)).length;

  return {
    shouldRender: districtGroups.length > 0,
    scope: 'district',
    key: 'districts',
    items: districtGroups.models,
    containsAdminDistricts: couldAdminCount > 0,
  };
};

export const getOwnedClasses = (ownedGroups: Groups, userCanOwnGroups: boolean): SectionInfo => {
  return {
    shouldRender: userCanOwnGroups,
    scope: 'class',
    key: 'classes-owned',
    items: ownedGroups.models,
  };
};

export const getSubscribedClasses = (subscribedGroups: Groups): SectionInfo => {
  return {
    shouldRender: true,
    scope: 'class',
    key: 'classes-subscribed',
    items: subscribedGroups.models,
  };
};

export const getRootInfo = (): SectionInfo => {
  return {
    shouldRender: true,
    scope: 'root',
    key: 'root',
    items: [rootGroup],
  };
};

/**
 * Eventually this should come from backend so that it's consistent across
 * clients.
 */
// $FlowFixMe
export function getOrderedSectionInfo({
    organizations = new Organizations(),
    districtGroups = new Groups(),
    schoolGroups = new Groups(),
    subscribedGroups = new Groups(),
    ownedGroups = new Groups(),
    userCanOwnGroups = false,
    options = {} }): SectionInfo[] {
  const opts = {
    ignoreDistrictSection: false,
    ignoreDistrictOrganizationSection: false,
    ...options,
  };

  const possibleSections = _.compact([
    getRootInfo(),
    opts.ignoreDistrictSection ? undefined : getDistrictsInfo(districtGroups),
    opts.ignoreDistrictOrganizationSection ? undefined : getDistrictsOrganizationInfo(organizations),
    getSchoolsInfo(organizations, schoolGroups),
    getOwnedClasses(ownedGroups, userCanOwnGroups),
    getSubscribedClasses(subscribedGroups),
  ]);

  const sectionsToRender = possibleSections.filter(s => s.shouldRender);

  const useAdminSortOrder = _.some(sectionsToRender, section => {
    return section.shouldRender && (
      section.scope === 'district_organization' ||
      section.containsAdminDistricts ||
      section.containsAdminSchools
    );
  });

  if (useAdminSortOrder) {
    return sectionsToRender.sort(adminComparator);
  }

  return sectionsToRender.sort(sectionOrdering);
}

// XXX transitioning to a shared interface for org groups and class groups
export const GroupOrOrgType = PropTypes.oneOfType([
  PropTypes.instanceOf(Group),
  PropTypes.instanceOf(Organization),
]);

declare type SectionInfo = {
  shouldRender: boolean,
  scope: string,
  key: string,
  items: (Group|Organization)[],
};

export const SectionShape = PropTypes.shape({
  shouldRender: PropTypes.bool.isRequired,
  scope: PropTypes.string.isRequired,
  key: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(GroupOrOrgType).isRequired,
});

/**
 * @param  {Section[]} sections returned by getOrderedSectionInfo()
 * @return {Group|Organization} the default item to show if there is no selection
 */
export function defaultItemOf(sections: SectionInfo[]) {
  for (const section of sections) {
    const firstItem = section.items[0];
    if (firstItem) {
      return firstItem;
    }
  }
  return null;
}



// WEBPACK FOOTER //
// ./src/groups/lib/groupNavigation.js